"use client";

import logger from "@/lib/logger";
import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	Download,
	FileVideo,
	Image,
	Loader,
	Music,
	Play,
	Settings,
	Upload,
	Video,
	Volume2,
	Zap,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import VideoPlayer from "@/components/VideoPlayer";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { useTranslations } from "next-intl";
import { useAccessibility } from "@/hooks/useAccessibility";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FileDropZone } from "@/components/FileDropZone";

// Define available output formats and their associated FFmpeg command fragments
const OUTPUT_FORMATS = [
	// --- VIDEO FORMATS (Primary Conversions) ---
	{
		label: "MP4 Video (H.264 Fast)",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: [
			"-c:v",
			"libx264",
			"-preset",
			"ultrafast",
			"-crf",
			"28",
			"-threads",
			"0",
			"-tune",
			"fastdecode",
		],
	},
	{
		label: "MP4 Video (H.264 Quality)",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: [
			"-c:v",
			"libx264",
			"-preset",
			"medium",
			"-crf",
			"23",
			"-threads",
			"0",
		],
	},
	{
		label: "MP4 4K→1080p (Fast Downscale)",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: [
			"-c:v",
			"libx264",
			"-preset",
			"medium",
			"-crf",
			"25",
			"-vf",
			"scale=1920:1080",
			"-threads",
			"0",
		],
	},
	{
		label: "Animated GIF (4s clip)",
		ext: "gif",
		mime: "image/gif",
		icon: Image,
		command: ["-f", "gif", "-ss", "0", "-t", "4", "-vf", "fps=10,scale=480:-1"],
	},
	{
		label: "WebM Video (VP8 Fast)",
		ext: "webm",
		mime: "video/webm",
		icon: Video,
		command: [
			"-c:v",
			"libvpx",
			"-c:a",
			"libvorbis",
			"-quality",
			"realtime",
			"-cpu-used",
			"8",
			"-b:v",
			"1M",
			"-b:a",
			"128k",
			"-threads",
			"0",
		],
	},
	{
		label: "AVI Video (MPEG-4 Fast)",
		ext: "avi",
		mime: "video/avi",
		icon: Video,
		command: ["-c:v", "mpeg4", "-qscale:v", "5", "-threads", "0"],
	},
	{
		label: "MOV (QuickTime Fast)",
		ext: "mov",
		mime: "video/quicktime",
		icon: Video,
		command: [
			"-c:v",
			"libx264",
			"-preset",
			"medium",
			"-crf",
			"28",
			"-threads",
			"0",
		],
	},
	{
		label: "MPEG Transport Stream",
		ext: "ts",
		mime: "video/mp2t",
		icon: Video,
		command: ["-c:v", "mpeg2video", "-q:v", "8", "-threads", "0"],
	},
	// --- AUDIO FORMATS (Extraction) ---
	{
		label: "MP3 Audio (Extract 192k)",
		ext: "mp3",
		mime: "audio/mp3",
		icon: Music,
		command: ["-vn", "-acodec", "libmp3lame", "-b:a", "192k"],
	},
	{
		label: "AAC Audio (Extract 128k)",
		ext: "aac",
		mime: "audio/aac",
		icon: Music,
		command: ["-vn", "-c:a", "aac", "-b:a", "128k"],
	},
	{
		label: "WAV Audio (Uncompressed)",
		ext: "wav",
		mime: "audio/wav",
		icon: Music,
		command: ["-vn", "-c:a", "pcm_s16le"],
	},
	{
		label: "OGG Audio",
		ext: "ogg",
		mime: "audio/ogg",
		icon: Music,
		command: ["-vn", "-c:a", "libvorbis"],
	},
	{
		label: "FLAC Audio (Lossless)",
		ext: "flac",
		mime: "audio/flac",
		icon: Music,
		command: ["-vn", "-c:a", "flac"],
	},
	// --- IMAGE FORMATS (Frame Extraction) ---
	{
		label: "PNG Image (1st frame)",
		ext: "png",
		mime: "image/png",
		icon: Image,
		command: ["-vframes", "1"],
	},
	{
		label: "JPEG Image (1st frame)",
		ext: "jpg",
		mime: "image/jpeg",
		icon: Image,
		command: ["-vframes", "1", "-q:v", "2"],
	},
	{
		label: "BMP Image (1st frame)",
		ext: "bmp",
		mime: "image/bmp",
		icon: Image,
		command: ["-vframes", "1"],
	},
	{
		label: "TIFF Image (1st frame)",
		ext: "tiff",
		mime: "image/tiff",
		icon: Image,
		command: ["-vframes", "1"],
	},
];

const getOptimalThreadCount = (
	performanceMode: "balanced" | "aggressive" = "balanced",
): number => {
	const cores = navigator.hardwareConcurrency || 4;

	if (performanceMode === "aggressive") {
		// Use all available cores for maximum performance
		return cores;
	}

	// Balanced mode: leave some cores for the browser
	return Math.max(2, Math.floor(cores * 0.75));
};

// Define optional processing flags
interface ProcessingOptions {
	silenceRemoval: {
		enabled: boolean;
		threshold: number; // in dB (-60 to -10)
		duration: number; // minimum silence duration in seconds (0.1 to 5)
	};
	audioDenoising: {
		enabled: boolean;
		strength: number; // noise reduction in dB (1 to 50)
	};
	videoDenoising: {
		enabled: boolean;
		spatial: number; // spatial denoising (1 to 10)
		temporal: number; // temporal denoising (1 to 10)
	};
	videoStabilization: {
		enabled: boolean;
		shakiness: number; // shakiness detection (1 to 10)
		accuracy: number; // accuracy (1 to 15)
	};
	volumeNormalization: {
		enabled: boolean;
		target: number; // target loudness in LUFS (-30 to -10)
	};
	frameRateConversion: {
		enabled: boolean;
		fps: number; // target frame rate (15, 24, 30, 60)
	};
	frameInterpolation: {
		enabled: boolean;
		targetFps: number; // target frame rate with interpolation (60)
		method: "minterpolate" | "fps" | "framerate"; // interpolation method
	};
	videoResize: {
		enabled: boolean;
		resolution: string; // resolution string like "1920x1080"
		aspectRatio: "keep" | "stretch" | "crop"; // how to handle aspect ratio
		quality: "fast" | "balanced" | "high"; // scaling algorithm quality
	};
}

const DEFAULT_PROCESSING_OPTIONS: ProcessingOptions = {
	silenceRemoval: {
		enabled: false,
		threshold: -30,
		duration: 1.0,
	},
	audioDenoising: {
		enabled: false,
		strength: 12,
	},
	videoDenoising: {
		enabled: false,
		spatial: 4,
		temporal: 6,
	},
	videoStabilization: {
		enabled: false,
		shakiness: 5,
		accuracy: 10,
	},
	volumeNormalization: {
		enabled: false,
		target: -16,
	},
	frameRateConversion: {
		enabled: false,
		fps: 30,
	},
	frameInterpolation: {
		enabled: false,
		targetFps: 60,
		method: "framerate",
	},
	videoResize: {
		enabled: false,
		resolution: "1920x1080",
		aspectRatio: "keep",
		quality: "balanced",
	},
};

const VideoConverter = () => {
	const t = useTranslations("videoConverterComponent");
	const { ffmpegInstance, isLoaded, isLoading, hardwareInfo, error, loadFFmpeg } =
		useFFmpeg();

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const {
		announce,
		announceProgress,
		announceError,
		announceSuccess,
		focusElement
	} = useAccessibility();
	
	const [inputFile, setInputFile] = useState<File | null>(null);
	// Default to the first format (MP4)
	const [selectedFormat, setSelectedFormat] = useState(OUTPUT_FORMATS[0]);
	const [outputURL, setOutputURL] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [statusLog, setStatusLog] = useState("Initializing...");
	const [progress, setProgress] = useState(0);
	const [startTime, setStartTime] = useState<number | null>(null);
	const [estimatedTimeRemaining, setEstimatedTimeRemaining] =
		useState<string>("");
	const [performanceMode, setPerformanceMode] = useState<
		"balanced" | "aggressive"
	>("balanced");
	const [processingOptions, setProcessingOptions] = useState<ProcessingOptions>(
		DEFAULT_PROCESSING_OPTIONS,
	);
	const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);


	// Helper function to calculate estimated time remaining
	const calculateTimeRemaining = useCallback(
		(currentProgress: number, startTime: number): string => {
			if (currentProgress <= 0) return "";

			const elapsed = Date.now() - startTime;
			const totalEstimated = elapsed / currentProgress;
			const remaining = totalEstimated - elapsed;

			if (remaining <= 0) return "Almost done...";

			const minutes = Math.floor(remaining / 60000);
			const seconds = Math.floor((remaining % 60000) / 1000);

			if (minutes > 0) {
				return `${minutes}m ${seconds}s remaining`;
			}
			return `${seconds}s remaining`;
		},
		[],
	);

	// Helper function to build processing filters with proper order
	const buildProcessingFilters = (options: ProcessingOptions, selectedFormat: any): string[] => {
		const videoFilters: string[] = [];
		const audioFilters: string[] = [];

		// CORRECT VIDEO FILTER ORDER (important for quality and compatibility):
		// 1. Denoising (clean up input first)
		// 2. Scaling/Resizing (change dimensions)
		// 3. Frame rate operations (temporal changes)

		// Step 1: Video denoising filter (apply first to clean input)
		if (options.videoDenoising.enabled) {
			videoFilters.push(
				`hqdn3d=${options.videoDenoising.spatial}:${options.videoDenoising.spatial}:${options.videoDenoising.temporal}:${options.videoDenoising.temporal}`,
			);
		}

		// Step 2: Video resize filter (apply after denoising, before frame rate changes)
		let hasFormatScale = false;
		let formatScaleFilter = "";

		// Check if the selected format has a built-in scale filter
		if (selectedFormat.command.includes("-vf")) {
			const vfIndex = selectedFormat.command.indexOf("-vf");
			if (vfIndex !== -1 && vfIndex + 1 < selectedFormat.command.length) {
				formatScaleFilter = selectedFormat.command[vfIndex + 1];
				hasFormatScale = formatScaleFilter.includes("scale");
			}
		}

		// Apply custom resize if enabled, or use format's built-in scale
		if (options.videoResize.enabled) {
			const [width, height] = options.videoResize.resolution.split("x");
			let scaleFilter = "";

			// Handle aspect ratio preservation with corrected syntax
			if (options.videoResize.aspectRatio === "keep") {
				// Keep aspect ratio and pad with black bars - use separate scale and pad filters
				let flags = "";
				if (options.videoResize.quality === "fast") {
					flags = ":flags=fast_bilinear";
				} else if (options.videoResize.quality === "high") {
					flags = ":flags=lanczos";
				}
				scaleFilter = `scale=iw*min(${width}/iw\\,${height}/ih):ih*min(${width}/iw\\,${height}/ih)${flags},pad=${width}:${height}:(ow-iw)/2:(oh-ih)/2:black`;
			} else if (options.videoResize.aspectRatio === "crop") {
				// Crop to fill the target dimensions - use separate scale and crop filters
				let flags = "";
				if (options.videoResize.quality === "fast") {
					flags = ":flags=fast_bilinear";
				} else if (options.videoResize.quality === "high") {
					flags = ":flags=lanczos";
				}
				scaleFilter = `scale=iw*max(${width}/iw\\,${height}/ih):ih*max(${width}/iw\\,${height}/ih)${flags},crop=${width}:${height}`;
			} else {
				// Stretch to fit (basic scale)
				let flags = "";
				if (options.videoResize.quality === "fast") {
					flags = ":flags=fast_bilinear";
				} else if (options.videoResize.quality === "high") {
					flags = ":flags=lanczos";
				}
				scaleFilter = `scale=${width}:${height}${flags}`;
			}

			videoFilters.push(scaleFilter);
		} else if (hasFormatScale) {
			// Use the format's built-in scale filter if no custom resize
			videoFilters.push(formatScaleFilter);
		}

		// Step 3: Frame rate conversion (apply after scaling)
		if (
			options.frameRateConversion.enabled &&
			!options.frameInterpolation.enabled
		) {
			videoFilters.push(`fps=${options.frameRateConversion.fps}`);
		}

		// Step 4: Frame interpolation (higher quality, slower) - takes precedence over basic frame rate conversion
		if (options.frameInterpolation.enabled) {
			if (options.frameInterpolation.method === "minterpolate") {
				// Use much more conservative minterpolate settings to prevent memory issues
				// Simplified configuration that's less likely to cause "Out of memory" errors
				videoFilters.push(
					`minterpolate=fps=${options.frameInterpolation.targetFps}:mi_mode=blend:mc_mode=aobmc:vsbmc=1:search_param=4`,
				);
			} else if (options.frameInterpolation.method === "framerate") {
				// Use framerate filter for better stability and timing consistency
				videoFilters.push(
					`framerate=fps=${options.frameInterpolation.targetFps}:interp_start=0:interp_end=255:scene=50`,
				);
			} else {
				// Simple fps filter (fastest but lowest quality)
				videoFilters.push(`fps=${options.frameInterpolation.targetFps}`);
			}
		}

		// AUDIO FILTER ORDER:
		// 1. Denoising (clean up first)
		// 2. Silence removal (remove unwanted parts)
		// 3. Volume normalization (adjust levels last)

		// Step 1: Audio denoising filter
		if (options.audioDenoising.enabled) {
			audioFilters.push(`afftdn=nr=${options.audioDenoising.strength}`);
		}

		// Step 2: Silence removal filter
		if (options.silenceRemoval.enabled) {
			audioFilters.push(
				`silenceremove=start_periods=1:stop_periods=-1:start_threshold=${options.silenceRemoval.threshold}dB:stop_threshold=${options.silenceRemoval.threshold}dB:start_silence=${options.silenceRemoval.duration}:stop_silence=${options.silenceRemoval.duration}`,
			);
		}

		// Step 3: Volume normalization filter (apply last)
		if (options.volumeNormalization.enabled) {
			audioFilters.push(`loudnorm=I=${options.volumeNormalization.target}`);
		}

		const filters: string[] = [];
		if (videoFilters.length > 0) {
			filters.push("-vf", videoFilters.join(","));
		}
		if (audioFilters.length > 0) {
			filters.push("-af", audioFilters.join(","));
		}

		return filters;
	};

	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			const shouldUseSingleThreaded =
				hardwareInfo.isLowEnd || !hardwareInfo.supportsSharedArrayBuffer;
			const statusMessage = shouldUseSingleThreaded
				? hardwareInfo.supportsSharedArrayBuffer
					? t("status.readySingleThreaded")
					: t("status.readyCompatibility")
				: t("status.readyMultiThreaded");
			setStatusLog(statusMessage);
		} else {
			setStatusLog(t("status.initializing"));
		}
	}, [error, isLoading, isLoaded, hardwareInfo]);

	// Set up FFmpeg progress listener
	useEffect(() => {
		if (ffmpegInstance && isProcessing) {
			const handleProgress = ({ progress }: { progress: number }) => {
				setProgress(progress);
				if (startTime) {
					setEstimatedTimeRemaining(
						calculateTimeRemaining(progress, startTime),
					);
				}
				
				// Announce progress to screen readers
				announceProgress(Math.round(progress * 100), 100, t("a11y.progressDescription"));
			};

			ffmpegInstance.on("progress", handleProgress);

			return () => {
				ffmpegInstance.off("progress", handleProgress);
			};
		}
	}, [ffmpegInstance, isProcessing, startTime, calculateTimeRemaining, announceProgress]);

	// 2. Handle file selection from the client's device
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setInputFile(file);
			setOutputURL(""); // Clear previous output
			setStatusLog(t("status.fileSelected", { name: file.name, format: selectedFormat.ext.toUpperCase() }));
			
			// Announce file selection to screen readers
			announce(t("a11y.fileSelected", { name: file.name, size: (file.size / 1024 / 1024).toFixed(1) }));
		}
	};

	// 4. The core conversion logic
	const convertFile = useCallback(async () => {
		if (!inputFile || !ffmpegInstance) return;

		setIsProcessing(true);
		setOutputURL("");
		setProgress(0);
		setStartTime(Date.now());
		setEstimatedTimeRemaining("");
		setStatusLog(t("status.processing", { name: inputFile.name, format: selectedFormat.ext.toUpperCase() }));
		
		// Announce conversion start
		announce(t("a11y.conversionStarted"), "polite");

		const inputFileName = `input.${inputFile.name.split(".").pop()}`;
		const outputFileName = `output.${selectedFormat.ext}`;

		try {
			// Check file size and adjust strategy for mobile devices
			const fileSizeMB = inputFile.size / (1024 * 1024);
			const memoryLimitMB = hardwareInfo.memory * 1024; // Convert GB to MB

			if (hardwareInfo.isLowEnd && fileSizeMB > memoryLimitMB * 0.3) {
				throw new Error(
					t("errors.fileTooLarge", { size: fileSizeMB.toFixed(1) }),
				);
			}

			setStatusLog(t("status.loadingFile"));

			// Write file to FFmpeg filesystem with memory-aware error handling
			try {
				await ffmpegInstance.writeFile(
					inputFileName,
					await fetchFile(inputFile),
				);
			} catch (writeError) {
				if (
					writeError instanceof Error &&
					writeError.message &&
					writeError.message.includes("memory")
				) {
					throw new Error(
						t("errors.outOfMemoryLoad"),
					);
				}
				throw writeError;
			}

			// Build command with adaptive threading and mobile optimizations
			let threadCount = getOptimalThreadCount(performanceMode);

			// Only limit threads on truly low-end devices or when SharedArrayBuffer is unavailable
			// Allow more threading for frame interpolation on capable devices
			if (hardwareInfo.isLowEnd || !hardwareInfo.supportsSharedArrayBuffer) {
				if (processingOptions.frameInterpolation.enabled) {
					// For frame interpolation, use more conservative threading to prevent memory issues
					if (processingOptions.frameInterpolation.method === "minterpolate") {
						// minterpolate is very memory intensive, use even fewer threads
						if (hardwareInfo.isUltraHighEnd) {
							threadCount = Math.max(2, Math.floor(threadCount * 0.5)); // Use 50% threads on ultra high-end
						} else if (hardwareInfo.isHighEnd) {
							threadCount = Math.max(2, Math.floor(threadCount * 0.4)); // Use 40% threads on high-end
						} else if (hardwareInfo.isMidRange) {
							threadCount = Math.max(1, Math.floor(threadCount * 0.25)); // Use 25% threads on mid-range
						} else {
							threadCount = 1; // Single-threaded only for low-end devices
						}
					} else {
						// For framerate and fps methods, use less restrictive threading
						if (hardwareInfo.isUltraHighEnd) {
							threadCount = Math.max(4, Math.floor(threadCount * 0.75)); // Use 75% threads on ultra high-end
						} else if (hardwareInfo.isHighEnd) {
							threadCount = Math.max(3, Math.floor(threadCount * 0.6)); // Use 60% threads on high-end
						} else if (hardwareInfo.isMidRange) {
							threadCount = Math.max(2, Math.floor(threadCount * 0.4)); // Use 40% threads on mid-range
						} else {
							threadCount = 1; // Single-threaded only for low-end devices
						}
					}
				} else {
					threadCount = hardwareInfo.isLowEnd ? 1 : Math.max(1, Math.floor(threadCount * 0.5));
				}
			} else {
				// For devices with full SharedArrayBuffer support, allow more aggressive threading
				if (processingOptions.frameInterpolation.enabled) {
					if (processingOptions.frameInterpolation.method === "minterpolate") {
						// minterpolate needs more conservative threading even on high-end devices
						threadCount = Math.max(2, Math.floor(threadCount * 0.6));
					} else {
						// For framerate and fps methods, use less restrictive threading
						threadCount = Math.max(2, Math.floor(threadCount * 0.8));
					}
				}
			}

			const baseCommand = ["-i", inputFileName];

			// Build processing filters with proper order and format integration
			const processingFilters = buildProcessingFilters(processingOptions, selectedFormat);

			// Apply mobile-specific optimizations and remove conflicting filters
			let optimizedCommand = [...selectedFormat.command];

			// Remove any existing -vf or -af from the format command since we handle them in processingFilters
			const filteredCommand = [];
			for (let i = 0; i < optimizedCommand.length; i++) {
				const arg = optimizedCommand[i];
				if (arg === "-vf" || arg === "-af") {
					// Skip this argument and the next one (the filter value)
					i++; // Skip the filter value
					continue;
				}
				filteredCommand.push(arg);
			}
			optimizedCommand = filteredCommand;

			if (hardwareInfo.isLowEnd) {
				// For low-end devices, use faster/lower quality presets
				optimizedCommand = optimizedCommand.map((arg) => {
					if (arg === "medium") return "ultrafast";
					if (arg === "fast") return "ultrafast";
					if (arg === "23") return "28"; // Higher CRF (lower quality, faster)
					return arg;
				});
			}

			// Replace '-threads', '0' in format command with actual thread count
			optimizedCommand = optimizedCommand.map((arg, index) => {
				if (arg === "-threads" && optimizedCommand[index + 1] === "0") {
					return arg; // Keep '-threads'
				}
				if (optimizedCommand[index - 1] === "-threads" && arg === "0") {
					return threadCount.toString(); // Replace '0' with actual thread count
				}
				return arg;
			});

			const fullCommand = [
				...baseCommand,
				...processingFilters,
				...optimizedCommand,
				outputFileName,
			];

			setStatusLog(t("status.runningConversion", { threads: threadCount }));

			// Debug conversion command
			logger.debug({ command: fullCommand }, "FFmpeg Conversion Command");
			logger.debug({ filters: processingFilters }, "Processing Filters Applied");
			logger.debug({ format: selectedFormat.label }, "Selected Format");
			logger.debug({
				videoResize: processingOptions.videoResize,
				frameRateConversion: processingOptions.frameRateConversion,
				frameInterpolation: processingOptions.frameInterpolation,
				videoDenoising: processingOptions.videoDenoising,
				audioDenoising: processingOptions.audioDenoising,
				silenceRemoval: processingOptions.silenceRemoval,
				volumeNormalization: processingOptions.volumeNormalization,
			}, "Processing Options");

			// Debug hardware detection
			logger.debug({
				cores: hardwareInfo.cores,
				memory: hardwareInfo.memory,
				isLowEnd: hardwareInfo.isLowEnd,
				isMidRange: hardwareInfo.isMidRange,
				isHighEnd: hardwareInfo.isHighEnd,
				isUltraHighEnd: hardwareInfo.isUltraHighEnd,
				supportsSharedArrayBuffer: hardwareInfo.supportsSharedArrayBuffer,
				threadCount,
			}, "Hardware Info");

			try {
				await ffmpegInstance.exec(fullCommand);
			} catch (execError) {
				// Check if this was a frame interpolation failure and attempt fallback
				if (processingOptions.frameInterpolation.enabled && 
					processingOptions.frameInterpolation.method === "minterpolate") {
					
					setStatusLog(t("status.interpolationFallbackBalanced"));
					
					// Try fallback with framerate filter
					const fallbackProcessingOptions = {
						...processingOptions,
						frameInterpolation: {
							...processingOptions.frameInterpolation,
							method: "framerate" as const
						}
					};
					
					const fallbackFilters = buildProcessingFilters(fallbackProcessingOptions, selectedFormat);
					const fallbackCommand = [
						...baseCommand,
						...fallbackFilters,
						...optimizedCommand,
						outputFileName,
					];
					
					try {
						await ffmpegInstance.exec(fallbackCommand);
						setStatusLog(t("status.interpolationCompletedBalanced"));
					} catch {
						// If framerate also fails, try simple fps
						setStatusLog(t("status.interpolationFallbackFast"));
						
						const simpleFallbackOptions = {
							...processingOptions,
							frameInterpolation: {
								...processingOptions.frameInterpolation,
								method: "fps" as const
							}
						};
						
						const simpleFallbackFilters = buildProcessingFilters(simpleFallbackOptions, selectedFormat);
						const simpleFallbackCommand = [
							...baseCommand,
							...simpleFallbackFilters,
							...optimizedCommand,
							outputFileName,
						];
						
						try {
							await ffmpegInstance.exec(simpleFallbackCommand);
							setStatusLog(t("status.interpolationCompletedFast"));
						} catch {
							// If all frame interpolation methods fail, disable it and try regular conversion
							setStatusLog(t("status.interpolationTooMuchMemory"));
							
							const noInterpolationOptions = {
								...processingOptions,
								frameInterpolation: {
									...processingOptions.frameInterpolation,
									enabled: false
								}
							};
							
							const noInterpolationFilters = buildProcessingFilters(noInterpolationOptions, selectedFormat);
							const noInterpolationCommand = [
								...baseCommand,
								...noInterpolationFilters,
								...optimizedCommand,
								outputFileName,
							];
							
							await ffmpegInstance.exec(noInterpolationCommand);
							setStatusLog(t("status.interpolationSkipped"));
						}
					}
				} else if (
					execError instanceof Error &&
					execError.message &&
					(execError.message.includes("memory") ||
						execError.message.includes("Out of memory") ||
						execError.message.includes("RangeError"))
				) {
					throw new Error(
						t("errors.outOfMemoryConvert"),
					);
				} else {
					throw execError;
				}
			}

			setStatusLog(t("status.readingFile"));

			// Read output file with memory error handling
			let data: Uint8Array;
			try {
				data = (await ffmpegInstance.readFile(outputFileName)) as Uint8Array;
			} catch (readError) {
				if (
					readError instanceof Error &&
					readError.message &&
					readError.message.includes("memory")
				) {
					throw new Error(
						t("errors.outOfMemoryRead"),
					);
				}
				throw readError;
			}

			// Create downloadable blob with memory monitoring
			try {
				const blob = new Blob([data as BlobPart], {
					type: selectedFormat.mime,
				});
				const url = URL.createObjectURL(blob);
				setOutputURL(url);
				setProgress(1);
				setEstimatedTimeRemaining("");

				setStatusLog(t("status.conversionComplete", { format: selectedFormat.ext.toUpperCase() }));
				
				// Announce success and focus download button
				announceSuccess(t("a11y.conversionComplete"));
				setTimeout(() => {
					focusElement("[data-download-button]", 500);
				}, 1000);
			} catch {
				throw new Error(
					t("errors.outOfMemoryDownload"),
				);
			}
		} catch (error) {
			let errorMessage = "Unknown error occurred";
			
			// More robust error handling for various error types
			try {
				if (error instanceof Error) {
					errorMessage = error.message || error.toString() || "Error instance without message";
				} else if (typeof error === 'string') {
					errorMessage = error;
				} else if (error && typeof error === 'object') {
					// Handle cases where error might be a WebAssembly RuntimeError or other object
					if ('message' in error && typeof error.message === 'string') {
						errorMessage = error.message;
					} else if ('toString' in error && typeof error.toString === 'function') {
						errorMessage = error.toString();
					} else {
						errorMessage = JSON.stringify(error);
					}
				} else {
					errorMessage = String(error || "Unknown error");
				}
			} catch (stringifyError) {
				// Fallback if even error handling fails
				errorMessage = "Error occurred during conversion (details unavailable)";
				logger.error({ err: stringifyError }, "Error while processing error");
			}
			
			setStatusLog(t("status.conversionFailed", { error: errorMessage }));
			logger.error({ err: error }, "FFmpeg Run Error");
			setProgress(0);
			setEstimatedTimeRemaining("");
			
			// Announce error to screen readers
			announceError(errorMessage);
		} finally {
			setIsProcessing(false);
			// Cleanup filesystem
			try {
				await ffmpegInstance.deleteFile(inputFileName);
				await ffmpegInstance.deleteFile(outputFileName);
			} catch {
				// Files may not exist, ignore cleanup errors
			}
		}
	}, [
		inputFile,
		ffmpegInstance,
		selectedFormat,
		performanceMode,
		hardwareInfo,
		processingOptions,
		announce,
		announceSuccess,
		announceError,
		focusElement,
	]);

	// Helper component to display the selected format icon
	const FormatIcon = selectedFormat.icon;


	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
			<div className="mx-auto p-2 sm:p-6">
				<header className="text-center mb-10">
					<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
						<FileVideo
							className="h-8 w-8 me-4 text-destructive"
							aria-hidden="true"
						/>
						{t("title")}
					</h1>
					<p className="mt-4 text-muted-foreground text-lg font-bold">
						{t("subtitle")}
					</p>
				</header>

				{/* Status Bar */}
				<Alert
					className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
				>
					<AlertTriangle className="h-4 w-4" aria-hidden="true" />
					<AlertDescription className="font-bold">{statusLog}</AlertDescription>
				</Alert>

				{/* Main Interface */}
				<div className="flex flex-col gap-6">
					{/* 1. Upload Section */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Upload className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFile")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
<FileDropZone
								accept="video/*"
								disabled={!isLoaded || isProcessing}
								onFiles={(files) => {
									const file = Array.from(files)[0];
									if (file) {
										handleFileChange({ target: { files } } as any);
									}
								}}
								label="Drop a video file here, or click to browse"
								activeLabel="Drop to add"
							/>
							{inputFile && (
								<p id="file-info" className="mt-3 text-sm font-bold">
									<span className="text-primary">{inputFile.name}</span> (
									{(inputFile.size / 1024 / 1024).toFixed(2)} MB)
								</p>
							)}
							{!isLoaded && (
								<div className="mt-3 text-yellow-600 flex items-center font-bold">
									<Loader
										className="w-4 h-4 me-2 animate-spin"
										aria-hidden="true"
									/>
									{t("fileInput.loading")}
								</div>
							)}
							{!inputFile && isLoaded && (
								<p
									id="file-help"
									className="mt-2 text-sm text-muted-foreground font-bold"
								>
									{t("fileInput.hint")}
								</p>
							)}
						</CardContent>
					</Card>

					{/* 2. Output Format Selection */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<FormatIcon className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFormat")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<label htmlFor="format-select" className="sr-only">
								{t("formatSelect.label")}
							</label>
							<Select
								value={OUTPUT_FORMATS.indexOf(selectedFormat).toString()}
								onValueChange={(value) => {
									const formatIndex = parseInt(value);
									const format = OUTPUT_FORMATS[formatIndex];
									if (format) {
										setSelectedFormat(format);
										setOutputURL("");
										setStatusLog(
											inputFile
												? t("status.formatChanged", { format: format.ext.toUpperCase() })
												: t("status.formatReady"),
										);
									}
								}}
								disabled={!isLoaded || isProcessing}
							>
								<SelectTrigger
									id="format-select"
									className="w-full"
									aria-describedby="format-help"
								>
									<SelectValue placeholder={t("formatSelect.placeholder")} />
								</SelectTrigger>
								<SelectContent>
									{OUTPUT_FORMATS.map((format, idx) => (
										<SelectItem key={idx} value={idx.toString()}>
											{format.label} (*.{format.ext})
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p
								id="format-help"
								className="mt-2 text-sm text-muted-foreground font-bold"
							>
								{t("formatSelect.selected", { label: selectedFormat.label })}
							</p>

							{/* {t("performance.title")} Selector */}
							<div className="mt-6">
								<h3 id="performance-mode-title" className="text-lg font-black mb-4 uppercase">
									{t("performance.title")}
								</h3>
								<div
									className="grid grid-cols-2 gap-4"
									role="radiogroup"
									aria-labelledby="performance-mode-title"
								>
									<Button
										variant={
											performanceMode === "balanced" ? "default" : "outline"
										}
										onClick={() => setPerformanceMode("balanced")}
										disabled={isProcessing}
										className="h-auto p-4 flex-col gap-2"
										role="radio"
										aria-checked={performanceMode === "balanced"}
									>
										<div className="font-black text-start w-full">{t("performance.balanced")}</div>
										<div className="text-xs opacity-75">
											{t("performance.cores", { used: hardwareInfo.balancedThreads, total: hardwareInfo.cores })}
										</div>
									</Button>
									<Button
										variant={
											performanceMode === "aggressive"
												? "destructive"
												: "outline"
										}
										onClick={() => setPerformanceMode("aggressive")}
										disabled={isProcessing}
										className="h-auto p-4 flex-col gap-2"
										role="radio"
										aria-checked={performanceMode === "aggressive"}
									>
										<div className="font-black text-start w-full">{t("performance.maximum")}</div>
										<div className="text-xs opacity-75">
											{t("performance.cores", { used: hardwareInfo.aggressiveThreads, total: hardwareInfo.cores })}
										</div>
									</Button>
								</div>
								<div className="mt-3 text-xs text-muted-foreground font-bold">
									{t("performance.hardware", { cores: hardwareInfo.cores, memory: hardwareInfo.memory })}
									{hardwareInfo.isUltraHighEnd && t("performance.ultraHighEnd")}
									{hardwareInfo.isHighEnd && !hardwareInfo.isUltraHighEnd && t("performance.highEnd")}
									{hardwareInfo.isMidRange && !hardwareInfo.isHighEnd && t("performance.midRange")}
									{hardwareInfo.isLowEnd && !hardwareInfo.isMidRange && t("performance.lowEnd")}
									{!hardwareInfo.supportsSharedArrayBuffer && t("performance.compatibilityMode")}
								</div>
							</div>
						</CardContent>
					</Card>

					{/* {t("steps.advancedOptions")} */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Settings className="w-6 h-6" aria-hidden="true" />
								{t("steps.advancedOptions")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<div className="mb-4">
								<Button
									variant="outline"
									onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
									className="w-full"
									aria-expanded={showAdvancedOptions}
								>
									<Zap className="w-4 h-4 me-2" aria-hidden="true" />
									{showAdvancedOptions ? t("advanced.hideOptions") : t("advanced.showOptions")}
								</Button>
							</div>

							{showAdvancedOptions && (
								<div className="space-y-6">
									{/* Silence Removal */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="silence-removal"
												checked={processingOptions.silenceRemoval.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														silenceRemoval: {
															...prev.silenceRemoval,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="silence-removal"
												className="text-sm font-medium"
											>
												{t("advanced.silenceRemoval")}
											</Label>
										</div>
										{processingOptions.silenceRemoval.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.silenceThreshold", { value: processingOptions.silenceRemoval.threshold })}
													</Label>
													<Slider
														value={[processingOptions.silenceRemoval.threshold]}
														onValueChange={([value]) =>
															setProcessingOptions((prev) => ({
																...prev,
																silenceRemoval: {
																	...prev.silenceRemoval,
																	threshold: value,
																},
															}))
														}
														min={-60}
														max={-10}
														step={1}
														className="mt-1"
													/>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.silenceMinDuration", { value: processingOptions.silenceRemoval.duration })}
													</Label>
													<Slider
														value={[processingOptions.silenceRemoval.duration]}
														onValueChange={([value]) =>
															setProcessingOptions((prev) => ({
																...prev,
																silenceRemoval: {
																	...prev.silenceRemoval,
																	duration: value,
																},
															}))
														}
														min={0.1}
														max={5}
														step={0.1}
														className="mt-1"
													/>
												</div>
											</div>
										)}
									</div>

									{/* {t("advanced.audioDenoising")} */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="audio-denoising"
												checked={processingOptions.audioDenoising.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														audioDenoising: {
															...prev.audioDenoising,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="audio-denoising"
												className="text-sm font-medium"
											>
												<Volume2 className="w-4 h-4 inline me-1" />
												Audio Denoising
											</Label>
										</div>
										{processingOptions.audioDenoising.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("advanced.audioStrength", { value: processingOptions.audioDenoising.strength })}
												</Label>
												<Slider
													value={[processingOptions.audioDenoising.strength]}
													onValueChange={([value]) =>
														setProcessingOptions((prev) => ({
															...prev,
															audioDenoising: {
																...prev.audioDenoising,
																strength: value,
															},
														}))
													}
													min={1}
													max={50}
													step={1}
													className="mt-1"
												/>
											</div>
										)}
									</div>

									{/* {t("advanced.videoDenoising")} */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="video-denoising"
												checked={processingOptions.videoDenoising.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														videoDenoising: {
															...prev.videoDenoising,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="video-denoising"
												className="text-sm font-medium"
											>
												<Video className="w-4 h-4 inline me-1" />
												Video Denoising
											</Label>
										</div>
										{processingOptions.videoDenoising.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.videoSpatial", { value: processingOptions.videoDenoising.spatial })}
													</Label>
													<Slider
														value={[processingOptions.videoDenoising.spatial]}
														onValueChange={([value]) =>
															setProcessingOptions((prev) => ({
																...prev,
																videoDenoising: {
																	...prev.videoDenoising,
																	spatial: value,
																},
															}))
														}
														min={1}
														max={10}
														step={1}
														className="mt-1"
													/>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.videoTemporal", { value: processingOptions.videoDenoising.temporal })}
													</Label>
													<Slider
														value={[processingOptions.videoDenoising.temporal]}
														onValueChange={([value]) =>
															setProcessingOptions((prev) => ({
																...prev,
																videoDenoising: {
																	...prev.videoDenoising,
																	temporal: value,
																},
															}))
														}
														min={1}
														max={10}
														step={1}
														className="mt-1"
													/>
												</div>
											</div>
										)}
									</div>

									{/* {t("advanced.volumeNormalization")} */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="volume-normalization"
												checked={processingOptions.volumeNormalization.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														volumeNormalization: {
															...prev.volumeNormalization,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="volume-normalization"
												className="text-sm font-medium"
											>
												Volume Normalization
											</Label>
										</div>
										{processingOptions.volumeNormalization.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("advanced.targetLoudness", { value: processingOptions.volumeNormalization.target })}
												</Label>
												<Slider
													value={[processingOptions.volumeNormalization.target]}
													onValueChange={([value]) =>
														setProcessingOptions((prev) => ({
															...prev,
															volumeNormalization: {
																...prev.volumeNormalization,
																target: value,
															},
														}))
													}
													min={-30}
													max={-10}
													step={1}
													className="mt-1"
												/>
											</div>
										)}
									</div>

									{/* {t("advanced.frameRateConversion")} */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="frame-rate-conversion"
												checked={processingOptions.frameRateConversion.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														frameRateConversion: {
															...prev.frameRateConversion,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="frame-rate-conversion"
												className="text-sm font-medium"
											>
												Frame Rate Conversion
											</Label>
										</div>
										{processingOptions.frameRateConversion.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("advanced.targetFps", { value: processingOptions.frameRateConversion.fps })}
												</Label>
												<Select
													value={processingOptions.frameRateConversion.fps.toString()}
													onValueChange={(value) =>
														setProcessingOptions((prev) => ({
															...prev,
															frameRateConversion: {
																...prev.frameRateConversion,
																fps: parseInt(value),
															},
														}))
													}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="15">{t("advanced.fps15")}</SelectItem>
														<SelectItem value="24">{t("advanced.fps24")}</SelectItem>
														<SelectItem value="30">{t("advanced.fps30")}</SelectItem>
														<SelectItem value="60">{t("advanced.fps60")}</SelectItem>
													</SelectContent>
												</Select>
											</div>
										)}
									</div>

									{/* Frame Interpolation */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="frame-interpolation"
												checked={processingOptions.frameInterpolation.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														frameInterpolation: {
															...prev.frameInterpolation,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="frame-interpolation"
												className="text-sm font-medium"
											>
												<Zap className="w-4 h-4 inline me-1" />
												{t("advanced.frameInterpolation")}
											</Label>
										</div>
										{processingOptions.frameInterpolation.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.targetFps", { value: processingOptions.frameInterpolation.targetFps })}
													</Label>
													<Select
														value={processingOptions.frameInterpolation.targetFps.toString()}
														onValueChange={(value) =>
															setProcessingOptions((prev) => ({
																...prev,
																frameInterpolation: {
																	...prev.frameInterpolation,
																	targetFps: parseInt(value),
																},
															}))
														}
													>
														<SelectTrigger className="mt-1">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="60">{t("advanced.fps60")}</SelectItem>
															<SelectItem value="120">{t("advanced.fps120")}</SelectItem>
														</SelectContent>
													</Select>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.interpolationMethod", { method: processingOptions.frameInterpolation.method === "minterpolate" ? t("advanced.methodHighQuality") : processingOptions.frameInterpolation.method === "framerate" ? t("advanced.methodBalanced") : t("advanced.methodFast") })}
													</Label>
													<Select
														value={processingOptions.frameInterpolation.method}
														onValueChange={(value) =>
															setProcessingOptions((prev) => ({
																...prev,
																frameInterpolation: {
																	...prev.frameInterpolation,
																	method: value as
																		| "minterpolate"
																		| "fps"
																		| "framerate",
																},
															}))
														}
													>
														<SelectTrigger className="mt-1">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="framerate">{t("advanced.methodBalancedRecommended")}</SelectItem>
															<SelectItem value="minterpolate">{t("advanced.methodHighQualityMotion")}</SelectItem>
															<SelectItem value="fps">{t("advanced.methodFastSimple")}</SelectItem>
														</SelectContent>
													</Select>
												</div>
												<Alert>
													<AlertTriangle className="h-4 w-4" aria-hidden="true" />
													<AlertDescription className="text-xs">
														{t("advanced.interpolationWarning")}
													</AlertDescription>
												</Alert>
											</div>
										)}
									</div>

									{/* {t("advanced.videoResize")} */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="video-resize"
												checked={processingOptions.videoResize.enabled}
												onCheckedChange={(checked) =>
													setProcessingOptions((prev) => ({
														...prev,
														videoResize: {
															...prev.videoResize,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="video-resize"
												className="text-sm font-medium"
											>
												<Video className="w-4 h-4 inline me-1" />
												Video Resize
											</Label>
										</div>
										{processingOptions.videoResize.enabled && (
											<div className="ms-6 space-y-3">
												{/* Resolution Selection */}
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.targetResolution", { value: processingOptions.videoResize.resolution })}
													</Label>
													<Select
														value={processingOptions.videoResize.resolution}
														onValueChange={(value) =>
															setProcessingOptions((prev) => ({
																...prev,
																videoResize: {
																	...prev.videoResize,
																	resolution: value,
																},
															}))
														}
													>
														<SelectTrigger className="mt-1">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="854x480">{t("advanced.res480")}</SelectItem>
															<SelectItem value="1280x720">{t("advanced.res720")}</SelectItem>
															<SelectItem value="1920x1080">{t("advanced.res1080")}</SelectItem>
															<SelectItem value="2560x1440">{t("advanced.res1440")}</SelectItem>
															<SelectItem value="3840x2160">{t("advanced.res4k")}</SelectItem>
															<SelectItem value="1024x768">{t("advanced.res4x3")}</SelectItem>
															<SelectItem value="1366x768">{t("advanced.resLaptop")}</SelectItem>
															<SelectItem value="1600x900">{t("advanced.res16x9")}</SelectItem>
														</SelectContent>
													</Select>
												</div>

												{/* Aspect Ratio Handling */}
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.aspectRatio", { value: processingOptions.videoResize.aspectRatio === "keep" ? t("advanced.aspectKeep") : processingOptions.videoResize.aspectRatio === "crop" ? t("advanced.aspectCrop") : t("advanced.aspectStretch") })}
													</Label>
													<Select
														value={processingOptions.videoResize.aspectRatio}
														onValueChange={(value) =>
															setProcessingOptions((prev) => ({
																...prev,
																videoResize: {
																	...prev.videoResize,
																	aspectRatio: value as
																		| "keep"
																		| "stretch"
																		| "crop",
																},
															}))
														}
													>
														<SelectTrigger className="mt-1">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="keep">{t("advanced.aspectKeep")}</SelectItem>
															<SelectItem value="crop">{t("advanced.aspectCrop")}</SelectItem>
															<SelectItem value="stretch">{t("advanced.aspectStretch")}</SelectItem>
														</SelectContent>
													</Select>
												</div>

												{/* Scaling Quality */}
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("advanced.scalingQuality", { value: processingOptions.videoResize.quality === "fast" ? t("advanced.scaleFast") : processingOptions.videoResize.quality === "high" ? t("advanced.scaleHigh") : t("advanced.scaleBalanced") })}
													</Label>
													<Select
														value={processingOptions.videoResize.quality}
														onValueChange={(value) =>
															setProcessingOptions((prev) => ({
																...prev,
																videoResize: {
																	...prev.videoResize,
																	quality: value as
																		| "fast"
																		| "balanced"
																		| "high",
																},
															}))
														}
													>
														<SelectTrigger className="mt-1">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="fast">{t("advanced.scaleFast")}</SelectItem>
															<SelectItem value="balanced">{t("advanced.scaleBalanced")}</SelectItem>
															<SelectItem value="high">{t("advanced.scaleHigh")}</SelectItem>
														</SelectContent>
													</Select>
												</div>

												<Alert>
													<AlertTriangle className="h-4 w-4" aria-hidden="true" />
													<AlertDescription className="text-xs">
														{t("advanced.resizeWarning")}
													</AlertDescription>
												</Alert>
											</div>
										)}
									</div>

									<Alert>
										<AlertTriangle className="h-4 w-4" aria-hidden="true" />
										<AlertDescription className="text-xs">
											{t("advanced.advancedWarning")}
										</AlertDescription>
									</Alert>
								</div>
							)}
						</CardContent>
					</Card>

					{/* 4. Process Button */}
					<div className="flex justify-center">
						<Button
							onClick={convertFile}
							size="lg"
							variant={
								isLoaded && inputFile && !isProcessing ? "default" : "outline"
							}
							disabled={!isLoaded || !inputFile || isProcessing}
							className="px-8 py-4 text-lg"
							aria-describedby="convert-help"
						>
							{isProcessing ? (
								<>
									<Loader
										className="w-5 h-5 me-3 animate-spin"
										aria-hidden="true"
									/>
									{t("convert.processing", { format: selectedFormat.ext.toUpperCase() })}
								</>
							) : (
								<>
									<Play className="w-5 h-5 me-3" aria-hidden="true" />
									{t("convert.run")}
								</>
							)}
						</Button>
					</div>
					{!isLoaded || !inputFile ? (
						<p
							id="convert-help"
							className="text-center text-sm text-muted-foreground font-bold mt-2"
						>
							{!isLoaded
								? t("convert.waitingLoad")
								: t("convert.selectFileHint")}
						</p>
					) : null}

                    {/* Progress Bar */}
				{isProcessing && (
					<Card className="mb-6 p-3 sm:p-6">
						<div className="flex justify-between items-center mb-4">
							<Badge variant="secondary" className="font-black text-lg">
								{t("progress.convertingTo", { format: selectedFormat.ext.toUpperCase() })}
							</Badge>
							<span className="text-lg font-black">
								{Math.round(progress * 100)}%
							</span>
						</div>
						<Progress
							value={progress * 100}
							className="mb-4"
							aria-label={t("progress.conversionProgress", { percent: Math.round(progress * 100) })}
						/>
						{estimatedTimeRemaining && (
							<div className="text-sm font-bold text-center text-muted-foreground">
								{estimatedTimeRemaining}
							</div>
						)}
					</Card>
				)}

					{/* 5. Preview & Download Section */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Download className="w-6 h-6" aria-hidden="true" />
								{t("steps.previewDownload")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							{outputURL ? (
								<div className="space-y-6">
									{/* Video Preview */}
									<div>
										<h4 className="text-md font-bold mb-3">{t("download.videoPreview")}</h4>
										<VideoPlayer
											videoUrl={outputURL}
											fileName={`converted_file.${selectedFormat.ext}`}
										/>
									</div>

									{/* Download Section */}
									<div className="flex flex-col items-center justify-center text-center space-y-4 pt-4 border-t">
										<Badge
											variant="secondary"
											className="text-lg font-black px-4 py-2"
										>
											{t("download.completed")}
										</Badge>
										<Button asChild size="lg" className="px-6 py-3">
											<a
												href={outputURL}
												download={`converted_file.${selectedFormat.ext}`}
												data-download-button
												aria-describedby="download-info"
												aria-label={t("download.downloadLabel", { format: selectedFormat.ext.toUpperCase() })}
												onClick={() => announce(t("download.downloadStarted", { ext: selectedFormat.ext }))}
											>
												<Download className="w-5 h-5 me-3" aria-hidden="true" />
												{t("download.downloadFile", { ext: selectedFormat.ext })}
											</a>
										</Button>
										<p
											id="download-info"
											className="text-sm text-muted-foreground font-bold"
										>
											{t("download.readyForDownload")}
										</p>
									</div>
								</div>
							) : (
								<div className="text-center min-h-[12rem] flex items-center justify-center">
									{isProcessing ? (
										<p className="text-primary font-bold text-lg">
											{t("download.processingMessage")}
										</p>
									) : (
										<p className="text-muted-foreground font-bold">
											{t("download.emptyMessage")}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Information */}
				<Alert className="mt-8">
					<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
					<AlertDescription className="font-bold">
						{t.rich("privacy", {
							code: (chunks) => (
								<code className="bg-muted p-1 rounded font-mono text-sm">
									{chunks}
								</code>
							),
						})}
					</AlertDescription>
				</Alert>
			</div>
			</div>
		</ErrorBoundary>
	);
};

export default VideoConverter;
